import { useCallback, useEffect } from 'react';
import EditExamReservationSettings from 'components/AppService/ExamV3Settings/ExamReservationSettings/EditExamReservationSettings';
import LoadingWrapper from 'components/LoadingWrapper';
import { fetchExamV3Mapping } from 'slices/appService.slice';
import {
  examV3ExamReservationSettingsLoadingSelector,
  fetchExamV3ExamReservationSettings,
  saveExamV3ExamReservationSettings,
} from 'slices/examV3ExamReservationSettings.slice';
import {
  examV3ReservationSettingsLoadingSelector,
  examV3ReservationSettingsSelector,
  fetchExamV3ReservationSettings,
} from 'slices/examV3Settings.slice';
import { fetchFields, fieldsLoading } from 'slices/field.slice';
import {
  fetchObjectMapping,
  requestObjectTypes,
  selectObjectTypesLoading,
} from 'slices/objectTypes.slice';
import {
  fetchReservationTemplates,
  reservationTemplatesSelector,
} from 'slices/reservationTemplate.slice';
import { useAppDispatch, useAppSelector } from 'slices/store';
import { fetchTimeZones } from 'slices/timeZone.slice';
import {
  Alert,
  Button,
  DefaultContentLayout,
  Icon,
} from '@timeedit/ui-components';
import intl from '../../../i18n/intl';

const language = intl.messages as Record<string, string>;

const ExamReservationSettings = () => {
  const dispatch = useAppDispatch();
  useEffect(() => {
    dispatch(fetchTimeZones());
    dispatch(fetchReservationTemplates());
    dispatch(requestObjectTypes());
    dispatch(fetchFields());
    dispatch(fetchObjectMapping());
    dispatch(fetchExamV3Mapping());
    dispatch(fetchExamV3ReservationSettings());
    dispatch(fetchExamV3ExamReservationSettings());
  }, []);

  const reservationSettings = useAppSelector(examV3ReservationSettingsSelector);
  const reservationTemplates = useAppSelector(reservationTemplatesSelector);

  const allFieldsLoading = useAppSelector(fieldsLoading);
  const allObjectTypesLoading = useAppSelector(selectObjectTypesLoading);
  const reservationSettingsLoading = useAppSelector(
    examV3ReservationSettingsLoadingSelector
  );
  const configLoading = useAppSelector(
    examV3ExamReservationSettingsLoadingSelector
  );

  const onSave = useCallback(
    () => dispatch(saveExamV3ExamReservationSettings()),
    []
  );

  const capResTemplate = reservationTemplates?.find(
    (templ) => templ.id === reservationSettings.capacityReservationTemplate
  );

  const sizeResTemplate = reservationTemplates?.find(
    (templ) => templ.id === reservationSettings.sizeReservationTemplate
  );

  const isLoading =
    allFieldsLoading ||
    allObjectTypesLoading ||
    configLoading ||
    reservationSettingsLoading;

  return (
    <DefaultContentLayout
      title="Exam reservation settings"
      subtitle="Configure what exam information is shown to students"
      actions={
        <Button type="primary" onClick={onSave}>
          {language.save_changes as string}
        </Button>
      }
    >
      <LoadingWrapper isLoading={isLoading}>
        {!isLoading && !reservationSettings.sizeReservationTemplate && (
          <Alert
            style={{ margin: '10px' }}
            title="Missing configuration"
            description="Please configure a reservation template for size reservations in the Reservation section to the left"
            type="error"
            showIcon
            icon={<Icon id="Error" alt="Error" />}
          />
        )}
        {reservationSettings.sizeReservationTemplate && (
          <EditExamReservationSettings
            capacityReservationTemplate={capResTemplate}
            sizeReservationTemplate={sizeResTemplate}
          />
        )}
      </LoadingWrapper>
    </DefaultContentLayout>
  );
};

export default ExamReservationSettings;
